/** @author Rajkumar Rathod */
import { safeCall } from './utils';
import type {
  WidgetAnyHandler,
  WidgetEventHandler,
  WidgetEventMap,
  WidgetEventName,
} from './types';

type AnyFn = (arg: never) => void;

export class Emitter {
  private handlers = new Map<string, Set<AnyFn>>();

  on<K extends WidgetEventName>(name: K, handler: WidgetEventHandler<K>): () => void;
  on(name: '*', handler: WidgetAnyHandler): () => void;
  on(name: string, handler: AnyFn): () => void {
    let set = this.handlers.get(name);
    if (!set) {
      set = new Set();
      this.handlers.set(name, set);
    }
    set.add(handler);
    return () => this.off(name as WidgetEventName, handler as never);
  }

  off<K extends WidgetEventName>(name: K | '*', handler: WidgetEventHandler<K> | WidgetAnyHandler): void {
    this.handlers.get(name)?.delete(handler as AnyFn);
  }

  once<K extends WidgetEventName>(name: K, handler: WidgetEventHandler<K>): () => void {
    const dispose = this.on(name, ((payload: WidgetEventMap[K]) => {
      dispose();
      handler(payload);
    }) as WidgetEventHandler<K>);
    return dispose;
  }

  emit<K extends WidgetEventName>(name: K, payload: WidgetEventMap[K]): void {
    // Copy before iterating: a handler may call off() or on() during dispatch.
    for (const fn of Array.from(this.handlers.get(name) ?? [])) {
      safeCall(() => (fn as unknown as WidgetEventHandler<K>)(payload), `handler for "${name}"`);
    }
    for (const fn of Array.from(this.handlers.get('*') ?? [])) {
      safeCall(
        () => (fn as unknown as WidgetAnyHandler)({ name, payload } as never),
        'wildcard handler'
      );
    }
  }

  clear(): void {
    this.handlers.clear();
  }
}
